import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { XpoLtlFeaturesService, XpoLtlLoggedInUserService } from '@xpo-ltl/ngx-ltl';
import { XpoLtlShipmentDescriptor, XpoLtlShipmentDetailsComponentConfig, XpoLtlShipmentDetailTabCd } from '@xpo-ltl/ngx-ltl-shipment-details';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { ConfigManagerProperties } from '../enums/config-manager-properties.enum';
import { FeatureTypes } from '../enums/feature-types.enum';


@Component({
  selector: 'app-shipment-details',
  templateUrl: './shipment-details.component.html',
  styleUrls: ['./shipment-details.component.scss']
})
export class ShipmentDetailsComponent {

  config$: Observable<XpoLtlShipmentDetailsComponentConfig>;
  shipmentId$: Observable<XpoLtlShipmentDescriptor>;
  activeTab$: Observable<XpoLtlShipmentDetailTabCd>;

  constructor(
    private route: ActivatedRoute,
    private featuresService: XpoLtlFeaturesService,
    private loggedInUserService: XpoLtlLoggedInUserService,
    private configManagerService: ConfigManagerService
  ) {
    this.activeTab$ = this.route.queryParamMap.pipe(
      take(1),
      map((params) => {
        return params.get('selectedTab') as XpoLtlShipmentDetailTabCd;
      })
    );

    // shipmentInstId can arrive either as a route param or as a query param, so look in both.
    this.shipmentId$ = combineLatest([this.route.paramMap, this.route.queryParamMap]).pipe(
      take(1),
      map(([routeParams, queryParams]) => {
        const proNbr = queryParams.get('proNbr');
        if (proNbr) {
          return { proNbr } as XpoLtlShipmentDescriptor;
        }

        const shipmentInstId = queryParams.get('shipmentInstId') ?? routeParams.get('shipmentInstId');
        if (shipmentInstId) {
          return { shipmentInstId: +shipmentInstId } as XpoLtlShipmentDescriptor;
        }

        throw new Error('At least one of shipmentInstId or proNbr must be provided');
      })
    );

    this.config$ = forkJoin({
      user: this.loggedInUserService
        .getLoggedInUser(this.configManagerService.getSetting<string>(ConfigManagerProperties.loggedInUserRoot))
        .pipe(take(1)),
      _featuresLoaded: this.featuresService.loadFeatures$(FeatureTypes).pipe(take(1)),
    }).pipe(
      map(({ user }) => {
        const sicCd = user?.sicCode ?? '';
        const appointmentDetailsIframeEnabled = this.featuresService.getFeatureFlag(sicCd, FeatureTypes.AppointmentDetailsIframe);

        const tabs = [...(this.defaultConfig().tabs ?? [])];
        if (appointmentDetailsIframeEnabled) {
          tabs.splice(1, 0, XpoLtlShipmentDetailTabCd.APPOINTMENTDETAILSIFRAME);
        }

        return { tabs };
      }),
      catchError(() => of(this.defaultConfig())),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private defaultConfig(): XpoLtlShipmentDetailsComponentConfig {
    return {
      tabs: [
        XpoLtlShipmentDetailTabCd.APPOINTMENTMANAGEMENT,
        XpoLtlShipmentDetailTabCd.DOCUMENTS,
        XpoLtlShipmentDetailTabCd.EDI,
        XpoLtlShipmentDetailTabCd.CLAIMS,
        XpoLtlShipmentDetailTabCd.DISPUTES,
        XpoLtlShipmentDetailTabCd.EXCEPTIONS,
        XpoLtlShipmentDetailTabCd.INVOICING,
        XpoLtlShipmentDetailTabCd.NOTES,
        XpoLtlShipmentDetailTabCd.OSND,
      ],
    };
  }
}
